import React, {
  useState,
  useCallback,
  memo,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import dynamic from 'next/dynamic';
import { algoliasearch } from 'algoliasearch';
import { useAlgoliaSearchKey } from '@context/AlgoliaSearchKeyContext';
import singletonRouter from 'next/router';
import Cookies from 'js-cookie';
import {
  Configure,
  InstantSearch,
  Pagination,
  useInstantSearch,
  useConnector,
} from 'react-instantsearch';
import connectHierarchicalMenu from 'instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu';
import connectRange from 'instantsearch.js/es/connectors/range/connectRange';
import connectSearchBox from 'instantsearch.js/es/connectors/search-box/connectSearchBox';
import aa from 'search-insights';
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs';
import { useStorePc } from '@hooks/useStorePc';
import { InsightsClient } from 'instantsearch.js/es/types/insights';

import { getCategoryPageId } from '@utils/algolia/getCategoryPageId';
import { getPromotionFilters } from '@utils/algolia/getPromotionFilters';
import { getAlgoliaRuleContexts } from '@utils/algolia/getAlgoliaRuleContexts';
import getRouting from '@utils/algolia/routing';
import { parseAlgoliaUrl } from '@utils/algolia/helpers';
import {
  getPriceAttribute,
  migratePriceRange,
} from '@utils/algolia/priceContext';
import { usePriceGroupKey } from '@hooks/usePriceGroupKey';

import Header from './Header';
import { Hits } from '../Hits';
import PlpPagination from './PlpPagination';
import Sidebar from './Sidebar';
import AllFiltersPanel from './AllFiltersPanel';
import { FiltersProvider } from './FiltersProvider';
import Container from '@components/global/Container';

import { getIndex } from '@utils/algolia/analytics';

import styles from './SearchResults.module.scss';
import {
  ProductTileTakeover,
  TakeoverTilePTT,
  TakeoverTileSize,
  Treatment,
} from '@adapters/prismic/types/plp';
import { AlgonomyPlacement } from '@components/global/Recommendations/Loader';
import { SkeletonCards } from '@components/global/Recommendations/SkeletonCards';
import { CollectionData } from '@adapters/prismic/types/plp';
import cn from 'classnames';
import ProductLineCarousel from '../ProductLineCarousel';
import { ProductLineCarouselType } from '@adapters/prismic/types/plp';
import type { PlpCrossLinkingData } from '_components/PlpCrossLinking/types';
import ProductLineCarouselV2 from '_components/ProductLineCarousel';
import Banner from './Banner';
import {
  FEATURE_FLAG_HIDE_LEGACY_PTT,
  FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT,
  FEATURE_FLAG_PLP_FILTERS_V2,
  FEATURE_FLAG_PLP_STICKY_FILTERS,
  FEATURE_FLAG_NEW_PLP_PAGINATION,
  FEATURE_FLAG_PTT_V2,
  PRODUCT_LINE_CAROUSEL_V2,
} from '@config/feature-flag';
import type { ProductStripDealsType } from '@adapters/prismic/types/redesignTypes';
import type { DealsGridData } from '_components/Products/DealsGrid/types';

const Recommendations = dynamic(
  () => import('@components/global/Recommendations'),
  {
    loading: () => <SkeletonCards count={6} />,
    ssr: false,
  }
);

type SearchResultsProps = {
  serverUrl: string;
  locale?: string;
  title?: string;
  displayBanner?: boolean;
  onStateChange?: (state: any) => void;
  isMobile: boolean;
  pttTiles: ProductTileTakeover[];
  productLineCarousel: ProductLineCarouselType | null;
  recommendations?: AlgonomyPlacement[];
  collectionData?: CollectionData | null;
  displayCoopComponents?: boolean;
  heroTitle?: string;
  userToken?: string;
  algoliaSearchKey?: string;
  /**
   * Merch-curated cross-linking band (ED-211). Not part of `collectionData`
   * because its product records are resolved per request, outside the collection
   * cache — see the PLP `getServerSideProps`.
   */
  crossLinking?: PlpCrossLinkingData | null;
  productStripDeals?: ProductStripDealsType[] | null;
  dealsGrid?: DealsGridData | null;
  dealsGridPriority?: boolean;
};

const sanitizeSlug = (slug: string) => {
  return slug.replace(/'/g, "\\'");
};

// On a hard reload the pricing context is SSR-resolved, so `priceAttribute` is
// stable from the first render and `routeToState` already keys the URL price
// range correctly — no migration needed. This handles the remaining case: a
// client-side login/logout (no SSR pass) flips `priceAttribute` mid-session,
// after `routeToState` has already run. The price widgets switch attribute and
// would otherwise find no range under the new key, so migrate the value across
// the flip. Defined at module scope (not inline) so its identity is stable
// across renders and the `prevAttr` ref persists.
const PriceContextSync = ({ priceAttribute }: { priceAttribute: string }) => {
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const prevAttr = useRef(priceAttribute);

  useEffect(() => {
    const from = prevAttr.current;
    if (from === priceAttribute) return;
    prevAttr.current = priceAttribute;

    // `setIndexUiState` always schedules a search, even for a no-op update, so
    // only call it when there is actually a range under the old key to move.
    // This keeps the common case (logged-in customer with no price filter)
    // from triggering an extra client-side query on hydration.
    if (indexUiState.range?.[from] === undefined) return;

    setIndexUiState((prev) => migratePriceRange(prev, from, priceAttribute));
  }, [priceAttribute, indexUiState, setIndexUiState]);

  return null;
};

// InstantSearch only applies `uiState.query` to a request when a search-box
// widget is mounted, so this mounts one unconditionally regardless of which
// filter layout (and which visible SearchBox) is active.
export const VirtualSearchBox = () => {
  useConnector(connectSearchBox, {});
  return null;
};

function SearchResults({
  serverUrl,
  locale,
  title,
  displayBanner = false,
  onStateChange,
  isMobile,
  pttTiles,
  productLineCarousel,
  recommendations,
  collectionData,
  displayCoopComponents,
  heroTitle,
  userToken,
  algoliaSearchKey,
  crossLinking = null,
  productStripDeals,
  dealsGrid,
  dealsGridPriority,
}: SearchResultsProps) {
  const algoliaKeyFromContext = useAlgoliaSearchKey();
  const appId =
    algoliaKeyFromContext?.appId ||
    process.env.NEXT_PUBLIC_ALGOLIA_APP_ID ||
    '';
  const searchKey = algoliaSearchKey || algoliaKeyFromContext?.searchKey || '';
  const effectiveUserToken =
    algoliaKeyFromContext?.userToken || userToken || '';
  const searchClient = useMemo(() => {
    const client = algoliasearch(appId, searchKey);

    if (!effectiveUserToken) {
      return client;
    }

    return {
      ...client,
      search(requests: any[], ...args: any[]) {
        return client.search(
          requests.map((request) => ({
            ...request,
            params: {
              ...(request?.params || {}),
              userToken: effectiveUserToken,
            },
          })),
          ...args
        );
      },
    } as typeof client;
  }, [appId, searchKey, effectiveUserToken]);
  const [displayMobileNav, setDisplayMobileNav] = useState(false);
  const closeNavigation = useCallback(() => {
    setDisplayMobileNav(false);
  }, []);
  const openNavigation = useCallback(() => {
    setDisplayMobileNav(true);
  }, []);
  const isStorePc = useStorePc();

  const indexName = getIndex(locale);

  // Sort/filter ranking moves with the customer's pricing context: each tier
  // ranks on the sale-aware `prices.<tier>.effectivePrice` it actually pays.
  // `usePriceGroupKey` is SSR-seeded, so this is correct on the first render
  // and routing keys the URL price range under the right attribute. A
  // client-side login/logout flip is reconciled by `PriceContextSync`.
  const priceGroupKey = usePriceGroupKey();
  const priceAttribute = getPriceAttribute(priceGroupKey);

  const { stateMapping, routerOptions } = getRouting(
    indexName,
    serverUrl,
    locale || 'en',
    priceAttribute
  );

  const categoryUrl =
    typeof window !== 'undefined'
      ? `${window?.location?.origin || 'https://www.mec.ca'}${
          window.location.pathname
        }${window.location.search}`
      : serverUrl;

  const categoryPageId = getCategoryPageId(categoryUrl, locale || 'en');

  const algoliaDebug = Cookies.get('algoliaDebug');
  if (algoliaDebug) {
    console.log(`algolia: categoryPageId ${categoryPageId}`);
  }

  const categoryFilter = categoryPageId
    ? `categoryPageId:'${sanitizeSlug(categoryPageId)}'`
    : '';

  const promotionFilters = getPromotionFilters(serverUrl, locale);
  const serverPathname = new URL(serverUrl).pathname;

  const {
    isCollectionPlp,
    urlFilters: { categorySlug },
  } = parseAlgoliaUrl(serverPathname, locale);

  const collectionFilters = isCollectionPlp
    ? categorySlug?.[1]?.split('?')[0]?.replace(/-/g, ' ') || ''
    : '';

  const searchFilters = isCollectionPlp
    ? `_collections:'${collectionFilters}'`
    : `${categoryFilter}${promotionFilters}`;

  const analyticsTags = [
    'mec',
    serverUrl.includes('/search') ? 'search' : 'browse',
    isMobile ? 'mobile' : 'desktop',
    isStorePc ? 'internal' : 'external',
  ];

  const routing = {
    router: createInstantSearchRouterNext({
      singletonRouter,
      serverUrl,
      routerOptions,
    }),
    stateMapping: stateMapping,
  };

  const handlePaginationClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Virtual widgets to ensure UI state consistency
  const VirtualHierarchicalMenu = () => {
    useConnector(connectHierarchicalMenu, {
      attributes: [
        'categories.lvl0',
        'categories.lvl1',
        'categories.lvl2',
        'categories.lvl3',
        'categories.lvl4',
        'categories.lvl5',
        'categories.lvl6',
        'categories.lvl7',
      ],
    });
    return null;
  };

  const VirtualRangeInput = () => {
    useConnector(connectRange, {
      attribute: priceAttribute,
    });
    return null;
  };

  const [, bestSellersRecommendations] = recommendations || [null, null];

  // would be great to grab this number from the algolia index settings rather than hardcoding
  const takeoverCells = pttTiles.reduce((total, tile) => {
    if (tile.treatment === Treatment.takeoverTile) {
      // Takeover tiles aren't rendered when the flag is off, so they take no cells
      if (!FEATURE_FLAG_PTT_V2) {
        return total;
      }
      return (
        total +
        ((tile as TakeoverTilePTT).product_tile.size === TakeoverTileSize.two
          ? 2
          : 1)
      );
    }
    // Legacy PTTs aren't rendered when hidden, so they take no cells either.
    if (FEATURE_FLAG_HIDE_LEGACY_PTT) {
      return total;
    }
    return total + 1;
  }, 0);
  const hitsPerPage = 52 - takeoverCells;
  const ruleContexts = getAlgoliaRuleContexts(categoryUrl);

  const headerRef = useRef<HTMLDivElement | null>(null);

  const searchLayout = (
    <div
      className={cn(styles.searchContainer, {
        [styles.searchContainerWithProductLineCarousel]:
          productLineCarousel !== null &&
          productLineCarousel?.items?.length > 0,
        [styles.searchContainerFullWidth]: FEATURE_FLAG_PLP_FILTERS_V2,
      })}
    >
      {!FEATURE_FLAG_PLP_FILTERS_V2 && (
        <Sidebar
          displayMobileNav={displayMobileNav}
          onCloseNavigation={closeNavigation}
          locale={locale}
          categoryUrl={categoryUrl}
          priceAttribute={priceAttribute}
        />
      )}

      {productLineCarousel !== null &&
        productLineCarousel?.items?.length > 0 && (
          <div className={styles.productLineCarousel}>
            {PRODUCT_LINE_CAROUSEL_V2 ? (
              <ProductLineCarouselV2
                title={title}
                productLineCarousel={productLineCarousel}
                headerRef={headerRef}
              />
            ) : (
              <ProductLineCarousel
                title={title}
                productLineCarousel={productLineCarousel}
                headerRef={headerRef}
              />
            )}
          </div>
        )}

      <div
        className={cn(styles.searchResults, {
          [styles.searchResultsStickyFilters]: FEATURE_FLAG_PLP_STICKY_FILTERS,
        })}
        ref={headerRef}
      >
        <Header
          locale={locale}
          banner={
            <Banner
              displayBanner={displayBanner}
              displayCoopComponents={displayCoopComponents}
              collectionData={collectionData}
              locale={locale}
              title={title}
            />
          }
          title={title}
          currentURL={categoryUrl}
          openMobileNavigation={openNavigation}
          recommendations={recommendations}
          isBannerDisplaying={displayBanner}
          heroTitle={heroTitle}
          productLineCarousel={productLineCarousel}
          productStripDeals={productStripDeals}
          dealsGrid={dealsGrid}
          dealsGridPriority={dealsGridPriority}
        />

        <div className={styles.searchHits}>
          <Hits
            locale={locale}
            pttTiles={pttTiles}
            isBannerDisplaying={displayBanner}
            videos={
              FEATURE_FLAG_HIDE_LEGACY_PLP_COLLECTION_CONTENT
                ? []
                : collectionData?.videos ?? []
            }
            crossLinking={crossLinking}
          />
        </div>
        {FEATURE_FLAG_NEW_PLP_PAGINATION ? (
          <PlpPagination
            className={styles.pagination}
            onNavigate={handlePaginationClick}
          />
        ) : (
          <Pagination
            showFirst={false}
            showLast={false}
            padding={2}
            onClick={handlePaginationClick}
            classNames={{
              root: styles.paginationRoot,
              list: styles.paginationList,
              item: styles.paginationItem,
              link: styles.paginationLink,
              selectedItem: styles.paginationSelectedItem,
              disabledItem: styles.paginationDisabledItem,
              nextPageItem: styles.paginationNextPageItem,
              previousPageItem: styles.paginationPreviousPageItem,
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        routing={routing}
        onStateChange={onStateChange}
        insights={{
          insightsClient: aa as InsightsClient,
        }}
        future={{
          preserveSharedStateOnUnmount: true,
        }}
      >
        <VirtualHierarchicalMenu />
        <VirtualRangeInput />
        <VirtualSearchBox />
        <PriceContextSync priceAttribute={priceAttribute} />
        {/*
          Don't set `userToken` here: it's injected into every request by the
          searchClient wrapper above, so adding it to <Configure> is redundant
          and — because the SSR render receives a `userToken` prop the client
          render does not — would make the server/client search params differ
          and force an extra query on mount instead of reusing initialResults.
        */}
        <Configure
          filters={searchFilters}
          clickAnalytics
          analyticsTags={analyticsTags}
          hitsPerPage={hitsPerPage}
          ruleContexts={ruleContexts}
          getRankingInfo
          maxValuesPerFacet={1000}
          facetingAfterDistinct={FEATURE_FLAG_PLP_FILTERS_V2 ? true : false}
        />

        {FEATURE_FLAG_PLP_FILTERS_V2 ? (
          <FiltersProvider
            locale={locale}
            categoryUrl={categoryUrl}
            priceAttribute={priceAttribute}
          >
            {searchLayout}
            <AllFiltersPanel />
          </FiltersProvider>
        ) : (
          searchLayout
        )}
      </InstantSearch>

      <div className={styles.recoWrapper}>
        <Container>
          {bestSellersRecommendations ? (
            <Recommendations
              placement={bestSellersRecommendations}
              isPlp
              key={bestSellersRecommendations.name}
            />
          ) : null}
        </Container>
      </div>
    </>
  );
}

const pttTilesKey = (tiles?: ProductTileTakeover[]) =>
  (tiles ?? [])
    .map((tile) => {
      const size =
        tile.treatment === Treatment.takeoverTile
          ? (tile as TakeoverTilePTT).product_tile.size
          : '';
      return `${tile.product_tile.id}:${tile.position}:${tile.treatment}:${size}`;
    })
    .join('|');

export default memo(SearchResults, (prevProps, nextProps) => {
  if (pttTilesKey(prevProps.pttTiles) !== pttTilesKey(nextProps.pttTiles)) {
    return false;
  }

  if (
    prevProps.productLineCarousel?.items?.length !==
    nextProps.productLineCarousel?.items?.length
  ) {
    return false;
  }

  if (prevProps.recommendations?.length !== nextProps.recommendations?.length) {
    return false;
  }

  if (prevProps?.collectionData !== nextProps?.collectionData) {
    return false;
  }

  if (prevProps?.crossLinking !== nextProps?.crossLinking) {
    return false;
  }

  if (prevProps?.title !== nextProps?.title) {
    return false;
  }

  return true;
});
